import axios from 'axios';
import { store } from '../store';
import { clearUser } from '../userSlice';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create centralized axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently handling logout to prevent race conditions
let isHandlingLogout = false;

// Queue to store failed requests during logout process
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ reject }) => {
    reject(error);
  });
  failedQueue = [];
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add any common headers or auth tokens here if needed
    // For debugging: console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling authentication errors
apiClient.interceptors.response.use(
  (response) => {
    // Reset logout flag on successful requests
    if (isHandlingLogout && response.status === 200) {
      isHandlingLogout = false;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized (token expired/invalid)
    if (error.response?.status === 401 && !isHandlingLogout) {
      
      // Don't redirect if we're already on login/register pages or other auth pages
      const currentPath = window.location.pathname;
      const isAuthPage = [
        '/login', 
        '/register', 
        '/forgot-password', 
        '/reset-password',
        '/invitation',
        '/join-request',
        '/join-org'
      ].some(path => currentPath.startsWith(path));
      
      if (isAuthPage) {
        // Just return the error without triggering logout on auth pages
        return Promise.reject(error);
      }
      
      // Prevent multiple simultaneous logout processes
      if (isHandlingLogout) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      isHandlingLogout = true;

      try {
        console.warn('Authentication failed - token expired or invalid');
        
        // Clear Redux state
        store.dispatch(clearUser());
        
        // Clear browser storage
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear any cookies (if using additional client-side cookies)
        document.cookie.split(";").forEach((c) => {
          const eqPos = c.indexOf("=");
          const name = eqPos > -1 ? c.substr(0, eqPos) : c;
          document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });

        // Process the queue of failed requests
        processQueue(new Error('Session expired'));

        // Show user-friendly message (only if not on auth pages)
        const shouldShowAlert = ![
          '/login', 
          '/register', 
          '/forgot-password', 
          '/reset-password',
          '/invitation',
          '/join-request',
          '/join-org'
        ].some(path => window.location.pathname.startsWith(path));
        
        if (shouldShowAlert) {
          // You can replace this with a toast notification or modal
          setTimeout(() => {
            alert('Your session has expired. Please log in again.');
          }, 100);
        }

        // Redirect to login page (only if not already on auth page)
        if (shouldShowAlert) {
          window.location.href = '/login';
        }
        
      } catch (logoutError) {
        console.error('Error during logout process:', logoutError);
      }

      // Return a rejected promise with a clear error message
      return Promise.reject(new Error('Session expired. Redirecting to login...'));
    }

    // Handle other HTTP errors with better error messages
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 403:
          console.warn('Access forbidden - insufficient permissions');
          error.message = 'You do not have permission to perform this action';
          break;
        case 404:
          console.warn('Resource not found');
          error.message = 'The requested resource was not found';
          break;
        case 429:
          console.warn('Rate limit exceeded');
          error.message = 'Too many requests. Please wait a moment and try again';
          break;
        case 500:
          console.error('Server error');
          error.message = 'Server error. Please try again later';
          break;
        default:
          // Use server error message if available
          error.message = data?.error || data?.message || `Request failed with status ${status}`;
      }
    } else if (error.request) {
      // Network error
      console.error('Network error:', error.message);
      error.message = 'Network error. Please check your connection and try again';
    } else {
      // Other error
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Helper function to check if we're currently handling logout (useful for components)
export const isLoggingOut = () => isHandlingLogout;

// Helper function for making requests with better error handling
export const makeRequest = async (requestFn) => {
  try {
    return await requestFn();
  } catch (error) {
    // Log error for debugging
    console.error('API Request failed:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
    });
    
    // Re-throw the error for component handling
    throw error;
  }
};

export default apiClient;